import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Loader2, Lightbulb, Trophy, Users, Filter, Download, Upload } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import { Button } from '../components/ui/button';
import { trackFunnelEvent } from '../services/funnelService';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import SEO from '../components/SEO';
import { CATEGORIES, EXPIRY_OPTIONS } from '../components/ideas/ideasConstants';
import { IdeaCard } from '../components/ideas/IdeaCard';
import { CreateIdeaModal } from '../components/ideas/CreateIdeaModal';
import { BulkUploadModal } from '../components/ideas/BulkUploadModal';
import { DownloadDataModal } from '../components/ideas/DownloadDataModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IdeasPage = () => {
  const { isAuthenticated, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [sortBy, setSortBy] = useState('votes');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const uploadFileRef = useRef(null);

  const [newIdea, setNewIdea] = useState({
    title: '',
    description: '',
    category: 'world_events',
    subcategory: '',
    outcomeType: 'binary',
    expiryOption: '30days',
    customDate: ''
  });
  const [creating, setCreating] = useState(false);

  const currentCategory = CATEGORIES.find(c => c.id === selectedCategory);

  useEffect(() => {
    fetchIdeas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedSubcategory, sortBy]);

  // Auto-open the create modal when arriving via ?create=1
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      if (isAuthenticated) {
        setShowCreateModal(true);
      } else {
        trackFunnelEvent('auth_shown', { source: 'homepage_banner' });
        setShowLoginPrompt(true);
      }
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, isAuthenticated, setSearchParams]);

  const fetchIdeas = async () => {
    try {
      setLoading(true);
      const categoryFilter = selectedSubcategory || (selectedCategory === 'all' ? null : selectedCategory);
      const data = await apiService.getIdeas(categoryFilter, null, sortBy, 50);
      setIdeas(data.ideas || []);
    } catch (error) {
      console.error('Failed to fetch ideas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (catId) => {
    setSelectedCategory(catId);
    setSelectedSubcategory(null);
  };

  const handleSubcategoryChange = (subId) => {
    setSelectedSubcategory(subId === selectedSubcategory ? null : subId);
  };

  const handleVote = async (ideaId, vote) => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    try {
      const updated = await apiService.voteIdea(ideaId, vote);
      setIdeas(prev => prev.map(idea =>
        idea.id === ideaId ? { ...idea, votes: updated.votes, user_vote: updated.user_vote } : idea
      ));
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  const handleEditIdea = async (ideaId, editData) => {
    try {
      const response = await fetch(`${API_URL}/api/ideas/${ideaId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        const updated = await response.json();
        setIdeas(prev => prev.map(idea =>
          idea.id === ideaId ? { ...idea, ...updated } : idea
        ));
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update');
      }
    } catch (error) {
      console.error('Failed to edit idea:', error);
      throw error;
    }
  };

  const handleSubmitClick = () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
    } else {
      setShowCreateModal(true);
    }
  };

  const calculateExpiryDate = () => {
    if (newIdea.expiryOption === 'custom' && newIdea.customDate) {
      return new Date(newIdea.customDate).toISOString();
    }
    const option = EXPIRY_OPTIONS.find(o => o.id === newIdea.expiryOption);
    if (option && option.days) {
      const date = new Date();
      date.setDate(date.getDate() + option.days);
      return date.toISOString();
    }
    return null;
  };

  const handleCreateIdea = async (e) => {
    e.preventDefault();
    if (!newIdea.title.trim() || !newIdea.description.trim()) return;

    const categoryObj = CATEGORIES.find(c => c.id === newIdea.category);
    if (categoryObj?.subcategories?.length > 0 && !newIdea.subcategory) {
      alert('Please select a subcategory');
      return;
    }

    setCreating(true);
    try {
      const expiresAt = calculateExpiryDate();
      const created = await apiService.createIdea(
        newIdea.title,
        newIdea.description,
        newIdea.category,
        newIdea.subcategory || null,
        newIdea.outcomeType,
        expiresAt
      );
      setIdeas(prev => [created, ...prev]);
      setShowCreateModal(false);
      setNewIdea({ title: '', description: '', category: 'world_events', subcategory: '', outcomeType: 'binary', expiryOption: '30days', customDate: '' });
      trackFunnelEvent('market_submitted', { source: 'ideas_page', userId: user?.user_id });
    } catch (error) {
      console.error('Failed to create idea:', error);
      if (error.message.includes('Authentication')) {
        setShowLoginPrompt(true);
        setShowCreateModal(false);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = (type, format) => {
    window.open(`${API_URL}/api/user/download/${type}?format=${format}`, '_blank');
  };

  const handleDownloadTemplate = (format) => {
    window.open(`${API_URL}/api/ideas/template/download?format=${format}`, '_blank');
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/ideas/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const result = await response.json();
      setUploadResult(result);

      if (result.created > 0) {
        fetchIdeas();
      }
    } catch (error) {
      setUploadResult({ errors: [error.message] });
    } finally {
      setUploading(false);
      if (uploadFileRef.current) {
        uploadFileRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
    } else {
      setShowUploadModal(true);
      setUploadResult(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050810]">
      <SEO
        title="Market Ideas - Submit & Vote"
        description="Submit and vote on ideas for new prediction markets on AMP Predict. The community decides what markets get listed. Contribute your market ideas today!"
        keywords="market ideas, prediction market ideas, community voting, submit market idea, AMP Predict ideas"
        url="https://amppredict.com/ideas"
      />
      <Header />

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#131b2e] to-[#050810] border-b border-[#1e2a45]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Lightbulb className="h-10 w-10 text-[#F97316]" />
              <h1 className="text-4xl font-bold text-white">Market Ideas</h1>
            </div>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
              Submit and vote on ideas for new prediction markets. The community decides what gets listed!
            </p>

            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Button
                onClick={handleSubmitClick}
                className="bg-gradient-to-b from-[#F97316] to-[#EA580C] hover:from-[#FB923C] hover:to-[#F97316] text-white rounded-full px-8 py-3 font-semibold shadow-[0_4px_0_0_#C2410C,0_6px_15px_rgba(249,115,22,0.3)] hover:shadow-[0_2px_0_0_#C2410C,0_4px_12px_rgba(249,115,22,0.3)] hover:translate-y-[2px] transition-all"
                data-testid="submit-new-idea-btn"
              >
                <Plus className="h-5 w-5 mr-2" />
                Submit New Idea
              </Button>

              <Button
                onClick={handleUploadClick}
                className="bg-gradient-to-b from-[#8B5CF6] to-[#7C3AED] hover:from-[#A78BFA] hover:to-[#8B5CF6] text-white rounded-full px-6 py-3 font-semibold shadow-[0_4px_0_0_#5B21B6,0_6px_15px_rgba(139,92,246,0.3)] hover:shadow-[0_2px_0_0_#5B21B6,0_4px_12px_rgba(139,92,246,0.3)] hover:translate-y-[2px] transition-all"
                data-testid="bulk-upload-btn"
              >
                <Upload className="h-5 w-5 mr-2" />
                Bulk Upload
              </Button>

              {isAuthenticated && (
                <Button
                  onClick={() => setShowDownloadModal(true)}
                  variant="outline"
                  className="border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45] rounded-full px-6 py-3"
                  data-testid="download-data-btn"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download My Data
                </Button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mt-10">
            <div className="text-center p-4 bg-[#1e2a45]/50 rounded-xl border border-[#1e2a45]">
              <Lightbulb className="h-6 w-6 text-[#F97316] mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">{ideas.length}</div>
              <div className="text-xs text-gray-500">Ideas</div>
            </div>
            <div className="text-center p-4 bg-[#1e2a45]/50 rounded-xl border border-[#1e2a45]">
              <Trophy className="h-6 w-6 text-[#3B82F6] mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">{ideas.filter(i => i.status === 'live').length}</div>
              <div className="text-xs text-gray-500">Live Markets</div>
            </div>
            <div className="text-center p-4 bg-[#1e2a45]/50 rounded-xl border border-[#1e2a45]">
              <Users className="h-6 w-6 text-green-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">{ideas.reduce((sum, i) => sum + Math.abs(i.votes), 0)}</div>
              <div className="text-xs text-gray-500">Total Votes</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-400">Category:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-[#3B82F6] text-white'
                    : 'bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56]'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-gray-400">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-[#1e2a45] text-white text-sm rounded-lg px-3 py-1.5 border border-[#1e2a45] focus:border-[#3B82F6] outline-none"
            >
              <option value="votes">Most Votes</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        </div>

        {/* Subcategory Tabs */}
        {currentCategory?.subcategories && currentCategory.subcategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6 pl-8 border-l-2 border-[#3B82F6]/30">
            <button
              onClick={() => setSelectedSubcategory(null)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                !selectedSubcategory
                  ? 'bg-[#3B82F6]/20 text-[#3B82F6] border border-[#3B82F6]/30'
                  : 'bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56]'
              }`}
            >
              All {currentCategory.name}
            </button>
            {currentCategory.subcategories.map(sub => (
              <button
                key={sub}
                onClick={() => handleSubcategoryChange(sub)}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-all capitalize ${
                  selectedSubcategory === sub
                    ? 'bg-[#3B82F6]/20 text-[#3B82F6] border border-[#3B82F6]/30'
                    : 'bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56]'
                }`}
              >
                {sub.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        )}

        {/* Ideas List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
            <span className="ml-3 text-gray-400">Loading ideas...</span>
          </div>
        ) : ideas.length === 0 ? (
          <div className="text-center py-20">
            <Lightbulb className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No ideas yet</h3>
            <p className="text-gray-400">Be the first to submit an idea!</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="ideas-list">
            {ideas.map(idea => (
              <IdeaCard
                key={idea.id}
                idea={idea}
                onVote={handleVote}
                onEdit={handleEditIdea}
                isAuthenticated={isAuthenticated}
                currentUserId={user?.user_id}
              />
            ))}
          </div>
        )}
      </div>

      <Footer />

      <AuthModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        initialView="login"
      />

      <DownloadDataModal
        open={showDownloadModal}
        onOpenChange={setShowDownloadModal}
        onDownload={handleDownload}
      />

      <CreateIdeaModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        newIdea={newIdea}
        setNewIdea={setNewIdea}
        onSubmit={handleCreateIdea}
        creating={creating}
      />

      <BulkUploadModal
        open={showUploadModal}
        onOpenChange={setShowUploadModal}
        uploadFileRef={uploadFileRef}
        uploading={uploading}
        uploadResult={uploadResult}
        onDownloadTemplate={handleDownloadTemplate}
        onUploadFile={handleUploadFile}
      />
    </div>
  );
};

export default IdeasPage;
